import { PresetExperiment } from '../types';
import { getComponentDef } from '../engine/componentLibrary';

function nodeData(type: Parameters<typeof getComponentDef>[0]) {
  const def = getComponentDef(type)!;
  return {
    componentType: def.type,
    label: def.label,
    category: def.category,
    icon: def.icon,
    description: def.description,
    tooltip: def.tooltip,
    ageMode: def.ageMode,
    properties: { ...def.defaultProperties },
    inputs: def.inputs,
    outputs: def.outputs,
  };
}

export const PRESET_EXPERIMENTS: PresetExperiment[] = [
  // ─── Experiment 1: Double Slit ─────────────────────────────────────────────
  {
    id: 'double-slit',
    name: '🌊 Double Slit Experiment',
    description:
      'Fire photons through two slits and watch the interference pattern emerge!',
    ageMode: ['beginner', 'intermediate', 'advanced'],
    concepts: ['superposition', 'interference', 'wave-particle-duality'],
    steps: [
      'Press ▶ Run to fire photons.',
      'Watch dots appear on the detector screen.',
      'After 100+ particles, notice the striped pattern.',
      'Try adding a Which-Path Detector to see what happens!',
    ],
    hint: 'The striped pattern is called an interference pattern. It only appears because each photon goes through BOTH slits at the same time!',
    nodes: [
      { id: 'n1', type: 'experimentNode', position: { x: 80, y: 200 }, data: nodeData('photon-source') },
      { id: 'n2', type: 'experimentNode', position: { x: 300, y: 200 }, data: nodeData('double-slit') },
      { id: 'n3', type: 'experimentNode', position: { x: 540, y: 200 }, data: nodeData('detector-screen') },
    ],
    edges: [
      { id: 'e1', source: 'n1', target: 'n2', sourceHandle: 'out', targetHandle: 'in' },
      { id: 'e2', source: 'n2', target: 'n3', sourceHandle: 'out', targetHandle: 'in' },
    ],
  },

  // ─── Experiment 2: Measurement Collapse ───────────────────────────────────
  {
    id: 'measurement-collapse',
    name: '👁 Measurement Collapses Superposition',
    description:
      'Add a detector at one slit and watch the interference pattern vanish!',
    ageMode: ['beginner', 'intermediate', 'advanced'],
    concepts: ['measurement', 'wave-particle-duality', 'decoherence'],
    steps: [
      'Notice the Which-Path Detector between the source and slit.',
      'Press ▶ Run and observe the screen.',
      'The striped pattern is GONE! Why?',
      'Remove the detector and run again to compare.',
    ],
    hint: 'The detector "looked" at which slit the particle used. This measurement forced the particle to choose ONE path – killing the interference!',
    nodes: [
      { id: 'n1', type: 'experimentNode', position: { x: 80, y: 200 }, data: nodeData('photon-source') },
      { id: 'n2', type: 'experimentNode', position: { x: 280, y: 200 }, data: nodeData('measurement-detector') },
      { id: 'n3', type: 'experimentNode', position: { x: 480, y: 200 }, data: nodeData('double-slit') },
      { id: 'n4', type: 'experimentNode', position: { x: 680, y: 200 }, data: nodeData('detector-screen') },
    ],
    edges: [
      { id: 'e1', source: 'n1', target: 'n2', sourceHandle: 'out', targetHandle: 'in' },
      { id: 'e2', source: 'n2', target: 'n3', sourceHandle: 'out', targetHandle: 'in' },
      { id: 'e3', source: 'n3', target: 'n4', sourceHandle: 'out', targetHandle: 'in' },
    ],
  },

  // ─── Experiment 3: Beam Splitter Superposition ────────────────────────────
  {
    id: 'beam-splitter',
    name: '◈ Beam Splitter Superposition',
    description:
      'A photon takes two paths at once through a beam splitter!',
    ageMode: ['intermediate', 'advanced'],
    concepts: ['superposition', 'randomness', 'wave-particle-duality'],
    steps: [
      'Press ▶ Run to fire photons at the beam splitter.',
      'See the probability graph – each path is 50/50.',
      'Run many times and notice the randomness.',
      'Try adding a Phase Shifter to change the probabilities.',
    ],
    hint: 'The photon is in superposition of both paths until measured. Each measurement is a random 50/50 event – true quantum randomness!',
    nodes: [
      { id: 'n1', type: 'experimentNode', position: { x: 80, y: 200 }, data: nodeData('photon-source') },
      { id: 'n2', type: 'experimentNode', position: { x: 300, y: 200 }, data: nodeData('beam-splitter') },
      { id: 'n3', type: 'experimentNode', position: { x: 520, y: 200 }, data: nodeData('probability-graph') },
    ],
    edges: [
      { id: 'e1', source: 'n1', target: 'n2', sourceHandle: 'out', targetHandle: 'in' },
      { id: 'e2', source: 'n2', target: 'n3', sourceHandle: 'transmitted', targetHandle: 'in' },
    ],
  },

  // ─── Experiment 4: Qubit Circuit ──────────────────────────────────────────
  {
    id: 'qubit-circuit',
    name: '🔵 Qubit + Hadamard Gate',
    description:
      'Build your first quantum circuit: |0⟩ → H → Measure',
    ageMode: ['advanced'],
    concepts: ['superposition', 'randomness', 'measurement'],
    steps: [
      'The qubit starts in |0⟩ (like a 0 bit).',
      'The Hadamard gate H puts it into superposition.',
      'Press ▶ Run to measure 500 times.',
      'Count how often you get 0 vs 1. Is it 50/50?',
    ],
    hint: 'The Hadamard gate is the key to quantum computing! It creates superposition, the foundation for quantum algorithms.',
    nodes: [
      { id: 'n1', type: 'experimentNode', position: { x: 80, y: 200 }, data: nodeData('qubit-source') },
      { id: 'n2', type: 'experimentNode', position: { x: 300, y: 200 }, data: nodeData('hadamard-gate') },
      { id: 'n3', type: 'experimentNode', position: { x: 520, y: 200 }, data: nodeData('measurement-counter') },
    ],
    edges: [
      { id: 'e1', source: 'n1', target: 'n2', sourceHandle: 'out', targetHandle: 'in' },
      { id: 'e2', source: 'n2', target: 'n3', sourceHandle: 'out', targetHandle: 'in' },
    ],
  },

  // ─── Experiment 5: Entanglement ───────────────────────────────────────────
  {
    id: 'entanglement',
    name: '🔗 Quantum Entanglement',
    description:
      'Two qubits that always agree – no matter how far apart!',
    ageMode: ['advanced'],
    concepts: ['entanglement', 'randomness', 'measurement'],
    steps: [
      'Two qubits are entangled (linked) together.',
      'Press ▶ Run to measure both 500 times.',
      'Look at the counter – they ALWAYS match!',
      'This happens even if the qubits are separated by light-years.',
    ],
    hint: 'Entanglement means two particles share a quantum state. Measuring one instantly determines the other – Einstein called it "spooky action at a distance"!',
    nodes: [
      { id: 'n1', type: 'experimentNode', position: { x: 80, y: 140 }, data: nodeData('qubit-source') },
      { id: 'n2', type: 'experimentNode', position: { x: 80, y: 260 }, data: nodeData('qubit-source') },
      { id: 'n3', type: 'experimentNode', position: { x: 340, y: 200 }, data: nodeData('hadamard-gate') },
      { id: 'n4', type: 'experimentNode', position: { x: 560, y: 200 }, data: nodeData('measurement-counter') },
    ],
    edges: [
      { id: 'e1', source: 'n1', target: 'n3', sourceHandle: 'out', targetHandle: 'in' },
      { id: 'e2', source: 'n2', target: 'n3', sourceHandle: 'out', targetHandle: 'in' },
      { id: 'e3', source: 'n3', target: 'n4', sourceHandle: 'out', targetHandle: 'in' },
    ],
  },
];
